import { useState } from "react"
import { useTranslation } from "react-i18next"
import { MagnifyingGlass } from "@phosphor-icons/react"
import { Button } from "@/components/ui/button"
import { Card, CardBody, CardHeader, CardHeaderTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { IdentifierValue } from "@/modules/admin/advanced/components/identifier-value"
import { fetchParsedNodeId, type ParsedNodeId } from "@/modules/admin/advanced/services/parseNodeIdQuery"

type NodeIdDecoding =
  | { status: "idle" }
  | { status: "invalidNodeId" }
  | { status: "requestFailed" }
  | { status: "decoded"; parsedNodeId: ParsedNodeId }

export const NodeIdDecoder = () => {
  const { t } = useTranslation()
  const [nodeId, setNodeId] = useState("")
  const [isDecoding, setIsDecoding] = useState(false)
  const [decoding, setDecoding] = useState<NodeIdDecoding>({ status: "idle" })

  const handleSubmit = async (event: React.SubmitEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (!nodeId.trim()) {
      return
    }

    setIsDecoding(true)
    try {
      const parsedNodeId = await fetchParsedNodeId(nodeId.trim())
      setDecoding(parsedNodeId ? { status: "decoded", parsedNodeId } : { status: "invalidNodeId" })
    } catch {
      setDecoding({ status: "requestFailed" })
    } finally {
      setIsDecoding(false)
    }
  }

  return (
    <Card className="h-fit w-120" data-testid="node-id-decoder-card">
      <CardHeader>
        <CardHeaderTitle className="flex items-center gap-4">{t("admin.advanced.nodeIdDecoder.title")}</CardHeaderTitle>
      </CardHeader>
      <CardBody className="flex flex-col gap-4 p-4 pt-0">
        <form className="flex items-center gap-2" onSubmit={handleSubmit}>
          <Input
            label={t("admin.advanced.nodeIdDecoder.inputLabel")}
            value={nodeId}
            onChange={(event) => setNodeId(event.target.value)}
            testId="node-id"
          />
          <Button
            type="submit"
            variant="tertiary"
            className="p-2"
            aria-label={t("admin.advanced.nodeIdDecoder.decode")}
            isLoading={isDecoding}
            disabled={isDecoding || !nodeId.trim()}
            data-testid="node-id-decode-button">
            <MagnifyingGlass className="text-on-surface-subdued" />
          </Button>
        </form>
        {decoding.status === "decoded" ? (
          <div className="flex flex-col gap-2">
            <IdentifierValue
              label={t("admin.advanced.nodeIdDecoder.typeName")}
              value={decoding.parsedNodeId.typeName}
              testId="node-id-type-name"
            />
            <IdentifierValue
              label={t("admin.advanced.nodeIdDecoder.internalId")}
              value={decoding.parsedNodeId.internalId}
              testId="node-id-internal-id"
            />
            <IdentifierValue
              label={t("admin.advanced.nodeIdDecoder.internalIdRuntimeType")}
              value={decoding.parsedNodeId.internalIdRuntimeType}
              testId="node-id-runtime-type"
            />
          </div>
        ) : null}
        {decoding.status === "invalidNodeId" || decoding.status === "requestFailed" ? (
          <p className="text-critical text-sm" data-testid="node-id-message">
            {t(`admin.advanced.nodeIdDecoder.${decoding.status}`)}
          </p>
        ) : null}
      </CardBody>
    </Card>
  )
}
